import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { ErrorMessagesService } from './error-messages.service';
import { BackgroundSettingsService } from './background-settings.service';
import { CapacitorNotificationService } from './capacitor-notification.service';
import { ToolConfigService } from './tool-config.service';
import { StorageService } from './storage.service';
import { LoggerService } from './logger.service';
import { ErrorHandlerService } from './error-handler.service';
import { NetworkService } from './network.service';
import { Company } from '../models/company.models';
import { Tool, ToolStep, UserTool } from '../models/tool.models';
import { UserJob } from '../models/job.models';
import { CACHE_DURATION, TIMING } from '../constants/app.constants';
import { ApiResponse, ApiRequest, GetAllTatUserDataResponse, GetAllTatUserDataRequest } from '../models/api.models';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegistrationCredentials {
  email: string;
  password: string;
  confirmPassword: string;
  dse: boolean;
}

export interface UserData {
  email?: string; // User email (for biometric unlock)
  app_bg: string;
  role?: string; // User role (e.g. 'owner', 'admin', 'user')
  language?: string; // User preferred language (ISO 639-1 code: de, en, tr, etc.)
  company_id?: number; // Reference to company
  user_id?: number; // User ID (optional, for reference)
  first_name?: string; // User first name
  last_name?: string; // User last name
}

/** Single reserved credits entry (tool + job combination) */
export interface ReservedCreditsDetail {
  tool_id: number;
  tool_key: string;
  tool_name: string;
  job_id: number;
  job_name: string;
  total_links: number;
  consumed: number;
  not_consumed: number;
  /** Open invitations released within Credits.expiring_soon_days days (incl. expired, not yet released); missing for older backends. */
  expiring_soon?: number;
  /** Earliest expiry of the open invitations (Unix seconds), null if none expires; missing for older backends. */
  next_expires_ts?: number | null;
}

export interface Credits {
  available_credits: number;
  reserved_credits: number;
  consumed_credits: number;
  total_credits: number;
  company_id: number | string; // Changed from user_id to company_id
  user_id?: number | string; // Optional, for reference
  /** Breakdown of reserved credits per tool/job */
  reserved_credits_detail?: ReservedCreditsDetail[];
  /** Window of ReservedCreditsDetail.expiring_soon in days; missing for older backends. */
  expiring_soon_days?: number;
}

// UserJob interface moved to src/app/models/job.models.ts

export interface UserDataItem {
  uid: string;
  sfa?: string; // SFA identifier (replaces uid in display)
  jid: string;
  lvl: string;
  lli: string;
  llit: number;
  overallScore?: string | number; // Overall score (1-5) for DM products
  score?: number[]; // Array of scores for KM products (9 scale values)
  perc_ranks?: number[] | Record<number, number>; // Percentile ranks for PM: array or object with keys 0-7 (leadership, achievement, teamwork, assertiveness, empathy, inventiveness, organisational, stress)
  started?: boolean; // First test entered (the tool consumes the credit then); missing for tools without this field
  valid_until?: number | null; // End of link validity in the tool (Unix seconds, incl. resets); missing for older tools
}

/** Change entry; tool is the userResults key the item was found under */
export type TatUserDataChangeItem = UserDataItem & { tool?: string };

export interface TatUserDataChanges {
  newEntries: TatUserDataChangeItem[];
  levelChanges: { item: TatUserDataChangeItem; oldLevel: string; newLevel: string }[];
  hasChanges: boolean;
}

/**
 * Unique key of a result item: uids are only unique within one tool database,
 * so the same uid may exist in several tools.
 */
export function getResultItemKey(toolName: string, uid: string | number | undefined): string {
  return `${String(toolName).trim().toLowerCase()}:${String(uid ?? '').trim().toLowerCase()}`;
}

export interface UserDataItemWithHighlight extends UserDataItem {
  isNew?: boolean;
  levelChanged?: boolean;
  oldLevel?: string;
  highlightType?: 'new' | 'level-changed' | 'none';
}

export interface AuthResponse {
  STATUS: string;
  DATA?: {
    auth_secret: string;
    message: string;
    status: {
      user_id: number;
      company_id?: number; // New: company_id
      tools: { [key: string]: Tool }; // API now sends "tools" key
      products?: { [key: string]: Tool }; // Legacy: kept for backward compatibility during migration
      user_data: UserData | any; // May contain mixed data from API (will be separated)
      company_data?: Company; // Company data (if API provides it separately)
      user_products: { [key: string]: UserTool };
      jobs?: { [key: string]: UserJob }; // Changed from user_jobs to jobs (company-related)
      user_jobs?: { [key: string]: UserJob }; // Backward compatibility
    };
  };
  DEBUG?: {
    runtime: string;
    memory: string;
  };
}

export interface StatusResponse {
  auth_staus: boolean;
  message: string;
  tools: { [key: string]: Tool }; // API now sends "tools" key
  products?: { [key: string]: Tool }; // Legacy: kept for backward compatibility during migration
  user_id: number;
  company_id?: number; // New: company_id
  user_products: { [key: string]: UserTool };
  user_data: UserData | any; // May contain mixed data from API (will be separated)
  company_data?: Company; // Company data (if API provides it separately)
  jobs?: { [key: string]: UserJob }; // Changed from user_jobs to jobs (company-related)
  user_jobs?: { [key: string]: UserJob }; // Backward compatibility
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private apiService = inject(ApiService);
  private errorMessagesService = inject(ErrorMessagesService);
  private backgroundSettingsService = inject(BackgroundSettingsService);
  private capacitorNotificationService = inject(CapacitorNotificationService);
  private toolConfigService = inject(ToolConfigService);
  private storageService = inject(StorageService);
  private logger = inject(LoggerService);
  private errorHandler = inject(ErrorHandlerService);

  private isAuthenticated = false;
  private userEmail: string | null = null;
  private userData: UserData | null = null; // Only User data (app_bg, role, company_id)
  private companyData: Company | null = null; // Only Company data (separated)
  private jobs: { [key: string]: UserJob } | null = null;
  private tools: { [key: string]: Tool } | null = null; // Tools (API sends as "products" but we treat as tools)
  private userResults: { [toolName: string]: { [uid: string]: UserDataItem } } | null = null; // Results from played tools (KulturMatcher, PersonalityMatcher, etc.)
  private lastTatUserDataChanges: TatUserDataChanges | null = null;
  private credits: Credits | null = null;
  private companyId: number | null = null; // New: company_id
  private userRole: string | null = null; // New: user role
  private userId: number | null = null; // Store user_id for tatUserData API calls
  private userResultsLoadPromise: Promise<void> | null = null; // Track loading promise to ensure it completes

  // Cache properties with timestamps
  private statusCache: { data: ApiResponse<StatusResponse> | null; timestamp: number } = { data: null, timestamp: 0 };
  private creditsCache: { data: Credits | null; timestamp: number } = { data: null, timestamp: 0 };
  private tatUserDataCache: { data: { data: { [key: string]: UserDataItem } | null; changes: TatUserDataChanges } | null; timestamp: number } = { data: null, timestamp: 0 };

  // Cache durations in milliseconds
  // Cache durations imported from constants
  private readonly CACHE_DURATION_STATUS = CACHE_DURATION.STATUS;
  private readonly CACHE_DURATION_CREDITS = CACHE_DURATION.CREDITS;
  private readonly CACHE_DURATION_TAT_DATA = CACHE_DURATION.TAT_USER_DATA;

  // Promise-sharing to prevent duplicate simultaneous calls
  private statusFetchPromise: Promise<ApiResponse<StatusResponse>> | null = null;
  private creditsFetchPromise: Promise<Credits | null> | null = null;
  private tatUserDataFetchPromise: Promise<{ data: { [key: string]: UserDataItem } | null; changes: TatUserDataChanges }> | null = null;

  private readonly networkService = inject(NetworkService);

  constructor() {
    // Check if user is already authenticated (e.g., from localStorage)
    this.checkAuthStatus();
  }

  /**
   * Safely set storage item with quota exceeded error handling
   * Uses StorageService which automatically handles Capacitor Preferences (native) or localStorage (web)
   * For large data, uses IndexedDB automatically
   * @param key - Storage key
   * @param value - Value to store
   * @param isCritical - If true, marks as critical data
   * @param isLarge - If true, uses IndexedDB for large data storage
   * @returns true if successful, false if failed
   */
  private async safeSetStorage(key: string, value: any, isCritical: boolean = false, isLarge: boolean = false): Promise<boolean> {
    try {
      if (isLarge) {
        // Use IndexedDB for large data (userResults, companyUsers, etc.)
        return await this.storageService.setLarge(key, value);
      } else {
        // Use Preferences/localStorage for small/medium data
        return await this.storageService.set(key, value);
      }
    } catch (error) {
      this.errorHandler.handleStorageError('set', key, error);
      return false;
    }
  }

  /**
   * @deprecated Use safeSetStorage instead. Kept for backward compatibility during migration.
   */
  private safeSetLocalStorage(key: string, value: any, isCritical: boolean = false): boolean {
    // Determine if this is large data that should use IndexedDB
    const isLarge = ['userResults', 'tatUserDataByTool', 'companyUsers', 'companyUsersActivationState'].includes(key);
    
    // Use async version but return synchronously for backward compatibility
    // This is a temporary bridge during migration
    this.safeSetStorage(key, value, isCritical, isLarge).catch(err => {
      this.errorHandler.handleStorageError('set', key, err);
    });
    
    // Also try synchronous localStorage as fallback for immediate compatibility
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, stringValue);
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.logger.warn(`localStorage quota exceeded for key "${key}". Using async storage...`);
        return false; // Will be handled by async storage
      }
      return false;
    }
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const data = {
        taskID: 'login',
        user_email: credentials.email,
        user_pass: credentials.password
      };

      const response: AuthResponse = await this.apiService.post(data);
      
      if (response.STATUS === 'SUCCESS-USER-LOGIN' && response.DATA) {
        this.isAuthenticated = true;
        this.userEmail = credentials.email;
        const rawJobs = response.DATA.status.jobs || response.DATA.status.user_jobs || null;
        this.jobs = this.normalizeJobsFromApi(rawJobs);
        // API now sends "tools", fallback to "products" for migration
        const rawTools = (response.DATA.status.tools || response.DATA.status.products) as { [key: string]: Tool } | null;
        this.tools = rawTools ? this.normalizeToolsFromApi(rawTools) : null;
        this.userId = response.DATA.status.user_id; // Store user_id for tatUserData API calls
        
        // Extract and store company_id
        this.companyId = response.DATA.status.company_id || 
                        (typeof response.DATA.status.user_data?.company_id === 'number' ? response.DATA.status.user_data.company_id : null) ||
                        (typeof response.DATA.status.user_data?.companyID === 'number' ? response.DATA.status.user_data.companyID : null);
        
        // Separate User and Company data from response
        const responseData = response.DATA.status.user_data || {};
        
        // Extract User data (app_bg, role, company_id, user_id, email, first_name, last_name)
        // Always use values from API response if available (including empty strings)
        // Check for property existence using 'in' operator to handle empty strings correctly
        this.userData = {
          email: credentials.email,
          app_bg: responseData.app_bg || '',
          role: ('role' in responseData) ? (responseData.role || undefined) : undefined,
          company_id: this.companyId || this.companyData?.company_id || undefined,
          user_id: this.userId || undefined,
          first_name: ('first_name' in responseData) ? (responseData.first_name || undefined) : undefined,
          last_name: ('last_name' in responseData) ? (responseData.last_name || undefined) : undefined
        };
        this.userRole = responseData.role || null;
        
        // Extract Company data (all company fields)
        // Check if API provides company_data separately, otherwise extract from user_data
        if (response.DATA.status.company_data) {
          this.companyData = response.DATA.status.company_data;
          // Extract company_id from company_data if not already set
          if (!this.companyId && this.companyData.company_id) {
            this.companyId = this.companyData.company_id;
          }
        } else {
          // Extract from user_data (backward compatibility)
          this.companyData = {
            company_id: this.companyId || 0,
            company: responseData.company || '',
            contact_name: responseData.contact_name || '',
            street: responseData.street || '',
            street_no: responseData.street_no || '',
            city: responseData.city || '',
            zip: responseData.zip || '',
            country: responseData.country || '',
            vat: responseData.vat || '',
            phone: responseData.phone || '',
            website: responseData.website || '',
            email_pp: responseData.email_pp || '',
            email_help: responseData.email_help || '',
            logo: responseData.logo || null,
            invarKEY: responseData.invarKEY || '',
            invarIV: responseData.invarIV || ''
          };
        }
        
        // Invalidate all caches after login since we have fresh data
        this.invalidateAllCaches();
        
        // Store auth status in localStorage for persistence (separated)
        // Only store essential data - redundant fields are removed
        this.safeSetLocalStorage('userData', this.userData, true);
        this.safeSetLocalStorage('companyData', this.companyData, true);
        this.safeSetLocalStorage('jobs', this.jobs, true);
        const toolsToStore = response.DATA.status.tools || response.DATA.status.products;
        if (toolsToStore) {
          this.safeSetLocalStorage('tools', toolsToStore as { [key: string]: any }, true); // Store as tools
        }
        this.safeSetLocalStorage('auth_secret', response.DATA.auth_secret, true);
        // Note: isAuthenticated, userEmail, companyId, userRole, userId are redundant
        // - isAuthenticated: can be derived from auth_secret existence
        // - userEmail: stored in userData.email
        // - companyId: stored in userData.company_id and companyData.company_id
        // - userRole: stored in userData.role
        // - userId: stored in userData.user_id
        
        // Background is now set in login.page.ts before navigation to ensure it's visible on login page
        // This prevents visible fade animation during navigation
        
        // Load credits data in background (don't await to avoid blocking login)
        this.getAvailableCredits().catch(error => {
          this.errorHandler.handleError('Credits-Load-After-Login', error);
        });
        
        return response;
      } else {
        const errorMessage = this.errorMessagesService.getErrorMessage(response.STATUS) || 
                           response.DATA?.message || 
                           this.errorMessagesService.getErrorMessage('UNKNOWN');
        
        return { 
          STATUS: response.STATUS,
          DATA: {
            auth_secret: '',
            message: errorMessage,
            status: {
              user_id: 0,
              tools: {},
              products: {}, // Legacy: kept for backward compatibility
              user_data: {} as UserData,
              user_products: {},
              jobs: {}
            }
          },
          DEBUG: response.DEBUG || { runtime: '0', memory: '0' }
        };
      }
    } catch (error) {
      // Use centralized error handler
      this.errorHandler.handleAuthError('login', error);
      const errorMessage = this.errorMessagesService.getErrorMessage('NETWORK') || 
                          this.errorHandler.getUserFriendlyMessage(error);
      
      return { 
        STATUS: 'NETWORK',
        DATA: {
          auth_secret: '',
          message: errorMessage,
          status: {
            user_id: 0,
            tools: {},
            products: {}, // Legacy: kept for backward compatibility
            user_data: {} as UserData,
            user_products: {},
            user_jobs: {}
          }
        },
        DEBUG: { runtime: '0', memory: '0' }
      };
    }
  }

  async register(credentials: RegistrationCredentials): Promise<AuthResponse> {
    try {
      const data = {
        taskID: 'registration',
        user_email: credentials.email,
        user_pass: credentials.password,
        user_pass_check: credentials.confirmPassword,
        user_dse: credentials.dse ? '1' : '0'
      };

      const response: AuthResponse = await this.apiService.post(data);
      
      if (response.STATUS === 'SUCCESS-USER-REGISTRATION') {
        // Don't authenticate user after registration - they need to activate via email
        // Just return the success response
        return response;
      } else {
        const errorMessage = this.errorMessagesService.getErrorMessage(response.STATUS) || 
                           response.DATA?.message || 
                           this.errorMessagesService.getErrorMessage('UNKNOWN');
        
        return { 
          STATUS: response.STATUS,
          DATA: {
            auth_secret: '',
            message: errorMessage,
            status: {
              user_id: 0,
              tools: {},
              products: {}, // Legacy: kept for backward compatibility
              user_data: {} as UserData,
              user_products: {},
              jobs: {}
            }
          },
          DEBUG: response.DEBUG || { runtime: '0', memory: '0' }
        };
      }
    } catch (error) {
      // Use centralized error handler
      this.errorHandler.handleAuthError('register', error);
      const errorMessage = this.errorMessagesService.getErrorMessage('NETWORK') || 
                          this.errorHandler.getUserFriendlyMessage(error);
      
      return { 
        STATUS: 'NETWORK',
        DATA: {
          auth_secret: '',
          message: errorMessage,
          status: {
            user_id: 0,
            tools: {},
            products: {}, // Legacy: kept for backward compatibility
            user_data: {} as UserData,
            user_products: {},
            user_jobs: {}
          }
        },
        DEBUG: { runtime: '0', memory: '0' }
      };
    }
  }

  async resetPassword(email: string): Promise<ApiResponse<{ message?: string | string[] }>> {
    try {
      const data = {
        taskID: 'password',
        user_email: email
      };

      const response: ApiResponse<{ message?: string | string[] }> = await this.apiService.post(data);
      
      if (response.STATUS === 'SUCCESS-AC_PASSWORD') {
        return response;
      } else {
        // Handle error cases - check for error messages in response
        let errorMessage = '';
        
        if (response.DATA?.message) {
          if (Array.isArray(response.DATA.message)) {
            // Check for specific error messages
            const errorMessages = response.DATA.message;
            if (errorMessages.some(msg => msg.includes('FAIL: found no user for'))) {
              errorMessage = `No user found with email ${email}`;
            } else if (errorMessages.some(msg => msg.includes('FAIL: update for activation_code failed'))) {
              errorMessage = this.errorMessagesService.getErrorMessage('PASSWORD_RESET_FAIL');
            } else if (errorMessages.some(msg => msg.includes('FAIL: sorry, email could not be sent'))) {
              errorMessage = this.errorMessagesService.getErrorMessage('PASSWORD_RESET_FAIL');
            } else {
              errorMessage = errorMessages.join(' ');
            }
          } else if (typeof response.DATA.message === 'string') {
            errorMessage = response.DATA.message;
          }
        }
        
        if (!errorMessage) {
          errorMessage = this.errorMessagesService.getErrorMessage('PASSWORD_RESET_FAIL');
        }
        
        return { 
          STATUS: response.STATUS,
          DATA: {
            message: errorMessage
          },
          DEBUG: response.DEBUG || { runtime: '0', memory: '0' }
        };
      }
    } catch (error) {
      // Use centralized error handler
      this.errorHandler.handleAuthError('resetPassword', error);
      const errorMessage = this.errorMessagesService.getErrorMessage('NETWORK') || 
                          this.errorHandler.getUserFriendlyMessage(error);
      
      return { 
        STATUS: 'NETWORK',
        DATA: {
          message: errorMessage
        },
        DEBUG: { runtime: '0', memory: '0' }
      };
    }
  }

  async changePassword(activationCode: string, newPassword: string): Promise<ApiResponse<{ message?: string | string[] }>> {
    try {
      const data = {
        taskID: 'setNewPassword',
        ac: activationCode,
        user_pass: newPassword,
        user_pass_check: newPassword
      };

      const response: ApiResponse<{ message?: string | string[] }> = await this.apiService.post(data);
      
      if (response.STATUS === 'SUCCESS-setNewPassword' || response.STATUS === 'SUCCESS-AC_PASSWORD') {
        return response;
      } else {
        // Handle error cases - check for error messages in response
        let errorMessage = '';
        
        if (response.DATA?.message) {
          if (Array.isArray(response.DATA.message)) {
            // Check for specific error messages
            const errorMessages = response.DATA.message;
            if (errorMessages.some(msg => msg.includes('FAIL: found no user for'))) {
              errorMessage = 'Invalid or expired activation code';
            } else if (errorMessages.some(msg => msg.includes('FAIL: update for activation_code failed'))) {
              errorMessage = this.errorMessagesService.getErrorMessage('PASSWORD_RESET_FAIL');
            } else if (errorMessages.some(msg => msg.includes('FAIL: sorry, email could not be sent'))) {
              errorMessage = this.errorMessagesService.getErrorMessage('PASSWORD_RESET_FAIL');
            } else {
              errorMessage = errorMessages.join(' ');
            }
          } else if (typeof response.DATA.message === 'string') {
            errorMessage = response.DATA.message;
          }
        }
        
        if (!errorMessage) {
          errorMessage = this.errorMessagesService.getErrorMessage('PASSWORD_RESET_FAIL');
        }
        
        return { 
          STATUS: response.STATUS,
          DATA: {
            message: errorMessage
          },
          DEBUG: response.DEBUG || { runtime: '0', memory: '0' }
        };
      }
    } catch (error) {
      // Use centralized error handler
      this.errorHandler.handleAuthError('changePassword', error);
      const errorMessage = this.errorMessagesService.getErrorMessage('NETWORK') || 
                          this.errorHandler.getUserFriendlyMessage(error);
      
      return { 
        STATUS: 'NETWORK',
        DATA: {
          message: errorMessage
        },
        DEBUG: { runtime: '0', memory: '0' }
      };
    }
  }

  logout(): void {
    this.isAuthenticated = false;
    this.userEmail = null;
    this.userData = null;
    this.companyData = null;
    this.jobs = null;
    this.tools = null;
    this.userResults = null;
    this.lastTatUserDataChanges = null;
    this.credits = null;
    this.companyId = null;
    this.userRole = null;
    this.userId = null;
    
    // Clear caches
    this.invalidateAllCaches();
    
    // Clear localStorage - only essential items
    localStorage.removeItem('userData');
    localStorage.removeItem('companyData');
    localStorage.removeItem('jobs');
    localStorage.removeItem('tools');
    // Also remove via StorageService for Capacitor Preferences
    this.storageService.remove('tools').catch(() => {});
    this.storageService.removeLarge('statusSnapshot').catch(() => {});
    localStorage.removeItem('auth_secret');
    localStorage.removeItem('userResults');
    // Remove old key for migration
    localStorage.removeItem('tatUserDataByTool');
    localStorage.removeItem('credits');
    // Clear company users data
    localStorage.removeItem('companyUsers');
    localStorage.removeItem('companyUsersActivationState');
    // Removed redundant items: isAuthenticated, userEmail, companyId, userRole, userId
    
    // Clear notification data
    this.capacitorNotificationService.resetAllData();
    
    // Reset background to default
    this.backgroundSettingsService.resetToDefault();
    
    // Navigate to login page and replace the entire navigation stack
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  /**
   * Invalidate all caches
   */
  invalidateAllCaches(): void {
    this.statusCache = { data: null, timestamp: 0 };
    this.creditsCache = { data: null, timestamp: 0 };
    this.tatUserDataCache = { data: null, timestamp: 0 };
    this.statusFetchPromise = null;
    this.creditsFetchPromise = null;
    this.tatUserDataFetchPromise = null;
  }

  /**
   * Invalidate status cache
   */
  invalidateStatusCache(): void {
    this.statusCache = { data: null, timestamp: 0 };
    this.statusFetchPromise = null;
  }

  /**
   * Invalidate credits cache
   */
  invalidateCreditsCache(): void {
    this.creditsCache = { data: null, timestamp: 0 };
    this.creditsFetchPromise = null;
  }

  /**
   * Invalidate tat user data cache
   */
  invalidateTatUserDataCache(): void {
    this.tatUserDataCache = { data: null, timestamp: 0 };
    this.tatUserDataFetchPromise = null;
  }

  isLoggedIn(): boolean {
    return this.isAuthenticated;
  }

  getUserEmail(): string | null {
    // Get from userData instead of separate property
    return this.userData?.email || null;
  }

  getUserData(): UserData | null {
    return this.userData;
  }

  getCompanyData(): Company | null {
    return this.companyData;
  }

  getCompanyId(): number | null {
    return this.companyId;
  }

  getUserRole(): string | null {
    return this.userRole;
  }

  /**
   * Normalize tools from API response
   * Processes steps array if present (new database structure) or keeps legacy step_1/2/3 fields
   */
  private normalizeToolsFromApi(tools: { [key: string]: Tool }): { [key: string]: Tool } {
    const normalized: { [key: string]: Tool } = {};
    
    for (const [key, tool] of Object.entries(tools)) {
      // If tool already has steps array, use it as-is (new structure)
      // Otherwise, keep legacy structure with step_1/2/3
      normalized[key] = {
        ...tool,
        // Steps array is optional - component will handle fallback to step_1/2/3
        steps: tool.steps || undefined
      };
    }
    
    return normalized;
  }

  /** Normalize jobs from API: map product_id to tool_id for backward compatibility */
  private normalizeJobsFromApi(rawJobs: { [key: string]: any } | null): { [key: string]: UserJob } | null {
    if (!rawJobs || typeof rawJobs !== 'object') return null;
    const normalized: { [key: string]: UserJob } = {};
    for (const [id, job] of Object.entries(rawJobs)) {
      if (job && typeof job === 'object') {
        const tool_id = job.tool_id ?? job.product_id;
        if (tool_id != null) {
          normalized[id] = { ...job, tool_id } as UserJob;
        }
      }
    }
    return Object.keys(normalized).length ? normalized : null;
  }

  getJobs(): { [key: string]: UserJob } | null {
    return this.jobs;
  }

  // Backward compatibility method
  getUserJobs(): { [key: string]: UserJob } | null {
    return this.getJobs();
  }

  getTools(): { [key: string]: Tool } | null {
    return this.tools;
  }

  getTatUserData(): { [key: string]: UserDataItem } | null {
    // Merge all items from userResults into a single object
    if (!this.userResults) {
      return null;
    }
    
    const mergedData: { [key: string]: UserDataItem } = {};
    Object.values(this.userResults).forEach(toolData => {
      Object.assign(mergedData, toolData);
    });
    
    return mergedData;
  }

  getUserResults(): { [toolName: string]: { [uid: string]: UserDataItem } } | null {
    return this.userResults;
  }

  getTatUserDataByTool(): { [toolName: string]: { [uid: string]: UserDataItem } } | null {
    return this.getUserResults();
  }

  getTatUserDataForTool(toolName: string): { [uid: string]: UserDataItem } | null {
    return this.userResults?.[toolName] || null;
  }

  /**
   * All result items with highlighting info, keyed by getResultItemKey(toolName, uid).
   */
  getTatUserDataWithHighlighting(): { [key: string]: UserDataItemWithHighlight } | null {
    if (!this.userResults) {
      return null;
    }

    // Helper function to normalize UID for comparison (entries without tool match by uid only)
    const normalizeUid = (uid: string | number | undefined): string => {
      if (!uid) return '';
      return String(uid).trim().toLowerCase();
    };
    const changeKeys = (item: TatUserDataChangeItem): string[] => {
      const uid = normalizeUid(item.uid);
      if (!uid) return [];
      return item.tool ? [getResultItemKey(item.tool, uid)] : [uid];
    };

    const highlightedData: { [key: string]: UserDataItemWithHighlight } = {};
    
    // Create maps for faster lookup
    const newEntryKeys = new Set<string>();
    const levelChangeMap = new Map<string, { item: UserDataItem; oldLevel: string; newLevel: string }>();
    
    if (this.lastTatUserDataChanges) {
      this.lastTatUserDataChanges.newEntries.forEach(newItem => {
        changeKeys(newItem).forEach(key => newEntryKeys.add(key));
      });
      
      this.lastTatUserDataChanges.levelChanges.forEach(change => {
        changeKeys(change.item).forEach(key => levelChangeMap.set(key, change));
      });
    }
    
    // Iterate over all tools and merge items
    Object.entries(this.userResults).forEach(([toolName, toolData]) => {
      Object.values(toolData).forEach(item => {
      const highlightedItem: UserDataItemWithHighlight = { ...item };
      const itemKey = getResultItemKey(toolName, item.uid);
      const itemUid = normalizeUid(item.uid);
      const isNew = newEntryKeys.has(itemKey) || (!!itemUid && newEntryKeys.has(itemUid));
      const levelChange = levelChangeMap.get(itemKey) ?? (itemUid ? levelChangeMap.get(itemUid) : undefined);
      
      if (isNew) {
        highlightedItem.isNew = true;
        highlightedItem.highlightType = 'new';
      } else if (levelChange) {
        highlightedItem.levelChanged = true;
        highlightedItem.oldLevel = levelChange.oldLevel;
        highlightedItem.highlightType = 'level-changed';
      } else {
        highlightedItem.highlightType = 'none';
      }
      
      highlightedData[itemKey] = highlightedItem;
      });
    });
    
    return highlightedData;
  }

  setLastTatUserDataChanges(changes: TatUserDataChanges): void {
    this.lastTatUserDataChanges = changes;
  }

  /**
   * Add newly detected changes to the not yet seen ones (until markTatUserDataAsSeen()).
   * A later fetch without changes must not remove the highlighting of earlier, unseen changes.
   */
  private addUnseenTatUserDataChanges(changes: TatUserDataChanges): void {
    const previous = this.lastTatUserDataChanges;
    if (!previous?.hasChanges) {
      this.lastTatUserDataChanges = changes;
      return;
    }
    if (!changes.hasChanges) {
      return;
    }

    const itemKey = (item: TatUserDataChangeItem) =>
      item.tool ? getResultItemKey(item.tool, item.uid) : String(item.uid ?? '').trim().toLowerCase();

    const newEntries = new Map(previous.newEntries.map(item => [itemKey(item), item]));
    changes.newEntries.forEach(item => newEntries.set(itemKey(item), item));

    const levelChanges = new Map(previous.levelChanges.map(change => [itemKey(change.item), change]));
    changes.levelChanges.forEach(change => {
      const key = itemKey(change.item);
      const earlier = levelChanges.get(key);
      // Keep the level the user saw last as oldLevel
      levelChanges.set(key, earlier ? { ...change, oldLevel: earlier.oldLevel } : change);
    });

    this.lastTatUserDataChanges = {
      newEntries: [...newEntries.values()],
      levelChanges: [...levelChanges.values()],
      hasChanges: true
    };
  }

  clearTatUserDataChanges(): void {
    this.lastTatUserDataChanges = null;
  }

  getLastTatUserDataChanges(): TatUserDataChanges | null {
    return this.lastTatUserDataChanges;
  }

  /**
   * Mark current tat user data as baseline to prevent re-detection of same changes
   * This should be called when user views the results page to acknowledge they've seen the changes
   * 
   * The current userResults becomes the new baseline for future comparisons.
   * This ensures that when the same data is fetched again, it won't be detected as "new changes".
   */
  async markTatUserDataAsSeen(): Promise<void> {
    // Clear the changes tracking so future comparisons won't detect the same changes again
    this.clearTatUserDataChanges();
    
    // The key insight: this.userResults already contains the current data
    // When compareTatUserDataByTool is called next time, it will compare new data
    // against this.userResults. Since we're not changing this.userResults,
    // the current state becomes the baseline.
    
    // However, we need to invalidate the cache so that the next fetch will do a fresh comparison
    // and update this.userResults with the latest data (which should be the same as current)
    // This ensures consistency between cache and baseline
    this.invalidateTatUserDataCache();
    
    // Save baseline to IndexedDB to ensure persistence across app restarts
    if (this.userResults) {
      // Remove old key for migration
      localStorage.removeItem('tatUserDataByTool');
      // Store to IndexedDB (large data) to ensure persistence
      await this.safeSetStorage('userResults', this.userResults, false, true);
    }
  }

  getAuthSecret(): string | null {
    return localStorage.getItem('auth_secret');
  }

  // Set authenticated state after successful biometric unlock
  // Returns the fetchStatus promise so caller can await and check for user data
  setAuthenticatedAfterBiometric(email: string): Promise<ApiResponse<StatusResponse>> {
    // Note: isAuthenticated is now derived from auth_secret, so no need to set it
    // Email will be loaded from userData after fetchStatus
    // Invalidate caches since we're re-authenticating
    this.invalidateAllCaches();
    // Return the promise so caller can await and verify user data exists
    return this.fetchStatus(true); // Force refresh to get latest data
  }

  private checkAuthStatus(): void {
    // Check if auth_secret exists (indicates authentication)
    const authSecret = this.getAuthSecret();
    
    if (authSecret) {
      // Set isAuthenticated based on auth_secret existence
      this.isAuthenticated = true;
      
      // Load all data from storage
      this.loadUserDataFromStorage();
      this.loadCompanyDataFromStorage();
      this.loadJobsFromStorage();
      this.loadToolsFromStorage().catch(error => {
        this.errorHandler.handleStorageError('load', 'tools', error);
      });
      this.loadCreditsFromStorage();
      // Load user results asynchronously and track the promise
      // This ensures we can wait for it to complete before fetching new data
      this.userResultsLoadPromise = this.loadUserResultsFromStorage().catch(err => {
        this.errorHandler.handleStorageError('loadUserResults', 'userResults', err);
      });
      
      // Extract redundant fields and cleanup
      this.extractRedundantFields();
      this.cleanupOldStorageKeys();
      
      // Set background from userData
      this.setBackgroundFromUserData();
    } else {
      // No auth_secret found, user is not authenticated
      this.handleUnauthenticatedState();
    }
  }

  /**
   * Load user data from storage
   */
  private loadUserDataFromStorage(): void {
    const storedUserData = localStorage.getItem('userData');
    if (!storedUserData) return;

    try {
      const parsed = JSON.parse(storedUserData);
      // Check if it's old format (contains company fields) and migrate
      if (parsed.company || parsed.contact_name) {
        // Old format - separate User and Company data
        this.userData = {
          email: parsed.email || undefined,
          app_bg: parsed.app_bg || '',
          role: parsed.role || undefined,
          language: parsed.language || undefined,
          company_id: parsed.company_id || parsed.companyID || undefined,
          user_id: parsed.user_id || undefined,
          first_name: parsed.first_name || undefined,
          last_name: parsed.last_name || undefined
        };
        // Extract Company data from old userData if not already loaded
        const storedCompanyData = localStorage.getItem('companyData');
        if (!storedCompanyData) {
          this.companyData = {
            company_id: parsed.company_id || parsed.companyID || 0,
            company: parsed.company || '',
            contact_name: parsed.contact_name || '',
            street: parsed.street || '',
            street_no: parsed.street_no || '',
            city: parsed.city || '',
            zip: parsed.zip || '',
            country: parsed.country || '',
            vat: parsed.vat || '',
            phone: parsed.phone || '',
            website: parsed.website || '',
            email_pp: parsed.email_pp || '',
            email_help: parsed.email_help || '',
            logo: parsed.logo || null,
            invarKEY: parsed.invarKEY || '',
            invarIV: parsed.invarIV || ''
          };
          // Save separated data
          this.safeSetLocalStorage('userData', this.userData, true);
          this.safeSetLocalStorage('companyData', this.companyData, true);
        }
      } else {
        // New format - already separated
        this.userData = parsed;
      }
    } catch (error) {
      this.errorHandler.handleStorageError('parse', 'userData', error);
    }
  }

  /**
   * Load company data from storage
   */
  private loadCompanyDataFromStorage(): void {
    const storedCompanyData = localStorage.getItem('companyData');
    if (!storedCompanyData) return;

    try {
      this.companyData = JSON.parse(storedCompanyData);
    } catch (error) {
      this.errorHandler.handleStorageError('parse', 'companyData', error);
    }
  }

  /**
   * Load jobs from storage (with migration support)
   */
  private loadJobsFromStorage(): void {
    const storedJobs = localStorage.getItem('jobs') || localStorage.getItem('userJobs');
    
    if (storedJobs) {
      try {
        this.jobs = this.normalizeJobsFromApi(JSON.parse(storedJobs));
        // Migrate old localStorage key
        if (localStorage.getItem('userJobs')) {
          localStorage.removeItem('userJobs');
          this.safeSetLocalStorage('jobs', this.jobs, true);
        }
      } catch (error) {
        this.errorHandler.handleStorageError('parse', 'jobs', error);
      }
    } else {
      // Try to migrate from old key
      const oldJobs = localStorage.getItem('userJobs');
      if (oldJobs) {
        try {
          this.jobs = this.normalizeJobsFromApi(JSON.parse(oldJobs));
          localStorage.removeItem('userJobs');
          this.safeSetLocalStorage('jobs', this.jobs, true);
        } catch (error) {
          this.errorHandler.handleStorageError('parse', 'userJobs', error);
          localStorage.removeItem('userJobs');
        }
      }
    }
  }

  /**
   * Load tools from storage
   */
  private async loadToolsFromStorage(): Promise<void> {
    // Try to load tools first
    try {
      const storedTools = await this.storageService.getJSON<{ [key: string]: Tool }>('tools');
      if (storedTools) {
        this.tools = storedTools;
        return;
      }
    } catch (error) {
      // Fallback to products for migration (one-time migration from old data)
    }

    // Migration fallback: try to load from products (for existing users)
    try {
      const storedProducts = await this.storageService.getJSON<{ [key: string]: Tool }>('products');
      if (storedProducts) {
        this.tools = storedProducts;
        // Migrate to tools key
        await this.storageService.set('tools', storedProducts);
        // Remove old products key after migration
        await this.storageService.remove('products').catch(() => {});
      }
    } catch (error) {
      // Also try localStorage directly as last fallback
      const storedProducts = localStorage.getItem('products');
      if (storedProducts) {
        try {
          this.tools = JSON.parse(storedProducts) as { [key: string]: Tool } | null;
          // Migrate to tools key
          localStorage.setItem('tools', storedProducts);
          localStorage.removeItem('products');
        } catch (parseError) {
          this.errorHandler.handleStorageError('parse', 'tools', parseError);
        }
      }
    }
  }

  /**
   * Load credits from storage (with migration support)
   */
  private loadCreditsFromStorage(): void {
    const storedCredits = localStorage.getItem('credits') || localStorage.getItem('userCredits');
    
    if (storedCredits) {
      try {
        this.credits = JSON.parse(storedCredits);
        // Migrate old localStorage key
        if (localStorage.getItem('userCredits')) {
          localStorage.removeItem('userCredits');
          this.safeSetLocalStorage('credits', this.credits, false);
        }
      } catch (error) {
        this.errorHandler.handleStorageError('parse', 'credits', error);
      }
    } else {
      // Try to migrate from old key
      const oldCredits = localStorage.getItem('userCredits');
      if (oldCredits) {
        try {
          this.credits = JSON.parse(oldCredits);
          localStorage.removeItem('userCredits');
          this.safeSetLocalStorage('credits', this.credits, false);
        } catch (error) {
          this.errorHandler.handleStorageError('parse', 'userCredits', error);
          localStorage.removeItem('userCredits');
        }
      }
    }
  }

  /**
   * Load user results from storage (with migration support)
   */
  private async loadUserResultsFromStorage(): Promise<void> {
    try {
      // Try new key first, then fallback to old keys for migration
      let storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('userResults');
      if (!storedUserResults) {
        storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('tatUserDataByTool');
        if (!storedUserResults) {
          storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('tatUserDataByProduct');
        }
        // Migrate old key if found
        if (storedUserResults) {
          await this.storageService.setLarge('userResults', storedUserResults);
          await this.storageService.removeLarge('tatUserDataByTool').catch(() => {});
          await this.storageService.removeLarge('tatUserDataByProduct').catch(() => {});
        }
      }
      if (storedUserResults && typeof storedUserResults === 'object' && !Array.isArray(storedUserResults)) {
        this.userResults = storedUserResults;
      }
    } catch (error) {
      this.errorHandler.handleStorageError('getLarge', 'userResults', error);
    }
  }

  /**
   * Extract redundant fields from userData and companyData (for backward compatibility)
   */
  private extractRedundantFields(): void {
    if (this.userData) {
      this.companyId = this.userData.company_id || null;
      this.userRole = this.userData.role || null;
      this.userId = this.userData.user_id || null;
      this.userEmail = this.userData.email || null;
    }
    
    // Also extract company_id from companyData if not already set
    if (!this.companyId && this.companyData?.company_id) {
      this.companyId = this.companyData.company_id;
    }
  }

  /**
   * Clean up old redundant localStorage entries
   */
  private cleanupOldStorageKeys(): void {
    // Remove old redundant entries
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('companyId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    
    // Clean up old localStorage keys (userJobs -> jobs, userCredits -> credits)
    if (localStorage.getItem('userJobs')) {
      localStorage.removeItem('userJobs');
    }
    if (localStorage.getItem('userCredits')) {
      localStorage.removeItem('userCredits');
    }
  }

  /**
   * Set background from userData
   */
  private setBackgroundFromUserData(): void {
    if (this.userData?.app_bg) {
      const currentBg = this.backgroundSettingsService.getCurrentBackground();
      if (currentBg !== this.userData.app_bg) {
        this.backgroundSettingsService.setStylesBeforeNavigation(this.userData.app_bg);
      } else {
        // Background is already correct, but still set header/toolbar heights
        this.backgroundSettingsService.setStylesBeforeNavigation(this.userData.app_bg);
      }
    } else {
      // No userData.app_bg found, set default background and styles immediately
      this.backgroundSettingsService.setStylesBeforeNavigation('bg1');
    }
  }

  /**
   * Handle unauthenticated state
   */
  private handleUnauthenticatedState(): void {
    this.isAuthenticated = false;
    // Set default background for non-authenticated users without animation (page reload)
    this.backgroundSettingsService.resetToDefaultImmediate();
  }

  // Method to check if user should be redirected to login
  canActivate(): boolean {
    if (!this.isLoggedIn()) {
      this.router.navigate(['/login']);
      return false;
    }
    return true;
  }

  // Method to fetch available credits from API with caching
  async getAvailableCredits(forceRefresh: boolean = false): Promise<Credits | null> {
    // Check cache first
    if (!forceRefresh && this.creditsCache.data && this.creditsCache.timestamp > 0) {
      const cacheAge = Date.now() - this.creditsCache.timestamp;
      if (cacheAge < this.CACHE_DURATION_CREDITS) {
        // Return cached data
        this.credits = this.creditsCache.data;
        return this.creditsCache.data;
      }
    }

    // If a fetch is already in progress, wait for it
    if (this.creditsFetchPromise) {
      return this.creditsFetchPromise;
    }

    // Start new fetch
    this.creditsFetchPromise = this.fetchCreditsInternal();
    
    try {
      const result = await this.creditsFetchPromise;
      return result;
    } finally {
      // Clear promise after completion
      this.creditsFetchPromise = null;
    }
  }

  private async fetchCreditsInternal(): Promise<Credits | null> {
    try {
      const authSecret = this.getAuthSecret();
      if (!authSecret) {
        return null;
      }

      const data = {
        taskID: 'getAvailableCredits',
        auth_secret: authSecret
      };

      const response: { STATUS: string; DATA?: Credits } = await this.apiService.post(data);

      if (response.STATUS === 'SUCCESS-getAvailableCredits' && response.DATA) {
        this.credits = response.DATA;
        // Update cache
        this.creditsCache = {
          data: response.DATA,
          timestamp: Date.now()
        };
        // Store in localStorage
        this.safeSetLocalStorage('credits', this.credits, false);
        return this.credits;
      }

      return null;
    } catch (error) {
      this.errorHandler.handleError('Credits-Fetch', error);
      return null;
    }
  }

  // Method to get available credits count (uses cached data if available, otherwise fetches from API)
  async getAvailableCreditsCount(): Promise<number> {
    // If we have cached data, use it
    if (this.credits?.available_credits !== undefined) {
      return this.credits.available_credits;
    }

    // Otherwise fetch from API
    const creditsData = await this.getAvailableCredits();
    if (creditsData?.available_credits !== undefined) {
      return creditsData.available_credits;
    }

    return 0;
  }

  // Synchronous method to get available credits count from cache (returns 0 if not cached)
  getAvailableCreditsCountSync(): number {
    return this.credits?.available_credits ?? 0;
  }

  // Method to get total credits count (uses cached data if available, otherwise fetches from API)
  async getTotalCreditsCount(): Promise<number> {
    // If we have cached data, use it
    if (this.credits?.total_credits !== undefined) {
      return this.credits.total_credits;
    }

    // Otherwise fetch from API
    const creditsData = await this.getAvailableCredits();
    if (creditsData?.total_credits !== undefined) {
      return creditsData.total_credits;
    }

    return 0;
  }

  // Synchronous method to get total credits count from cache (returns 0 if not cached)
  getTotalCreditsCountSync(): number {
    return this.credits?.total_credits ?? 0;
  }

  // Method to get user credits
  getCredits(): Credits | null {
    return this.credits;
  }

  // Backward compatibility method
  getUserCredits(): Credits | null {
    return this.getCredits();
  }

  // Method to fetch current status from server with caching
  /**
   * Fetches the current authentication status from the API
   * Uses caching to avoid excessive API calls
   * 
   * @param forceRefresh - If true, bypasses cache and fetches fresh data from API
   * @returns Promise resolving to API response containing status data (auth status, user data, jobs, tools, etc.)
   * 
   * @example
   * ```typescript
   * const status = await authService.fetchStatus();
   * if (status.DATA?.auth_staus === true) {
   *   // User is authenticated
   * }
   * ```
   */
  async fetchStatus(forceRefresh: boolean = false): Promise<ApiResponse<StatusResponse>> {
    // Check cache first
    if (!forceRefresh && this.statusCache.data && this.statusCache.timestamp > 0) {
      const cacheAge = Date.now() - this.statusCache.timestamp;
      if (cacheAge < this.CACHE_DURATION_STATUS) {
        // Return cached data and update local state
        const cachedResponse = this.statusCache.data;
        const cachedData = cachedResponse.DATA;
        if (cachedData?.auth_staus === true && cachedData?.message === 'logged_in') {
          // Merge jobs from cache with existing jobs from localStorage
          // If cache has jobs, use them (they are the latest)
          // If cache doesn't have jobs, keep existing jobs from localStorage
          const cachedJobs = cachedData.jobs || cachedData.user_jobs;
          if (cachedJobs && Object.keys(cachedJobs).length > 0) {
            // Cache has jobs, use them
            this.jobs = this.normalizeJobsFromApi(cachedJobs);
          } else {
            // Cache doesn't have jobs, load from localStorage as backup
            if (!this.jobs) {
              this.loadJobsFromStorage();
            }
            // If we still don't have jobs after loading from storage, set to null
            if (!this.jobs) {
              this.jobs = null;
            }
          }
          // API now sends "tools", fallback to "products" for migration
          const cachedTools = (cachedData.tools || cachedData.products) as { [key: string]: Tool } | null;
          this.tools = cachedTools ? this.normalizeToolsFromApi(cachedTools) : null;
          this.userId = cachedData.user_id; // Store user_id from cached response
          
          // Extract and separate User and Company data
          const responseData = cachedData.user_data || {};
          this.companyId = cachedData.company_id || 
                          (typeof responseData.company_id === 'number' ? responseData.company_id : null) ||
                          (typeof responseData.companyID === 'number' ? responseData.companyID : null);
          
          // Extract User data
          // Always use values from API response if available (including empty strings), otherwise preserve existing values
          // Check for property existence using 'in' operator to handle empty strings correctly
          this.userData = {
            email: this.userData?.email || undefined, // Preserve existing email
            app_bg: responseData.app_bg || '',
            role: ('role' in responseData) ? (responseData.role || undefined) : (this.userData?.role || undefined),
            company_id: this.companyId || this.companyData?.company_id || undefined,
            user_id: this.userId || undefined,
            first_name: ('first_name' in responseData) ? (responseData.first_name || undefined) : (this.userData?.first_name || undefined),
            last_name: ('last_name' in responseData) ? (responseData.last_name || undefined) : (this.userData?.last_name || undefined)
          };
          this.userRole = responseData.role || null;
          
          // Extract Company data
          if (cachedData.company_data) {
            this.companyData = cachedData.company_data;
            // Extract company_id from company_data if not already set
            if (!this.companyId && this.companyData.company_id) {
              this.companyId = this.companyData.company_id;
            }
          } else {
            // Extract from user_data (backward compatibility)
            this.companyData = {
              company_id: this.companyId || 0,
              company: responseData.company || '',
              contact_name: responseData.contact_name || '',
              street: responseData.street || '',
              street_no: responseData.street_no || '',
              city: responseData.city || '',
              zip: responseData.zip || '',
              country: responseData.country || '',
              vat: responseData.vat || '',
              phone: responseData.phone || '',
              website: responseData.website || '',
              email_pp: responseData.email_pp || '',
              email_help: responseData.email_help || '',
              logo: responseData.logo || null,
              invarKEY: responseData.invarKEY || '',
              invarIV: responseData.invarIV || ''
            };
          }
        }
        return cachedResponse;
      }
    }

    // If a fetch is already in progress, wait for it
    if (this.statusFetchPromise) {
      return this.statusFetchPromise;
    }

    // Start new fetch
    this.statusFetchPromise = this.fetchStatusInternal();
    
    try {
      const result = await this.statusFetchPromise;
      return result;
    } finally {
      // Clear promise after completion
      this.statusFetchPromise = null;
    }
  }

  private async fetchStatusInternal(): Promise<ApiResponse<StatusResponse>> {
    try {
      const authSecret = this.getAuthSecret();
      if (!authSecret) {
        throw new Error('No authentication secret found');
      }

      // Load jobs from localStorage BEFORE API call as backup
      // This ensures we have the latest jobs even if API doesn't return them
      const backupJobs = this.jobs || (() => {
        const storedJobs = localStorage.getItem('jobs') || localStorage.getItem('userJobs');
        if (storedJobs) {
          try {
            return JSON.parse(storedJobs);
          } catch (error) {
            this.errorHandler.handleStorageError('parse', 'jobs', error);
            return null;
          }
        }
        return null;
      })();

      // Check if offline - if so, try to return cached data
      if (this.networkService.isOffline()) {
        this.logger.log('Device is offline, attempting to use cached status data');
        
        // Check if we have cached status data
        if (this.statusCache.data && this.statusCache.timestamp > 0) {
          const cacheAge = Date.now() - this.statusCache.timestamp;
          // Use cache if it's less than 24 hours old (more lenient for offline)
          if (cacheAge < 24 * 60 * 60 * 1000) {
            this.logger.log('Using cached status data (offline mode)');
            return this.statusCache.data;
          }
        }
        
        // Ensure async storage hydration has completed (tools may still be loading from Storage/IndexedDB)
        if (!this.tools) {
          await this.loadToolsFromStorage();
        }

        // Try persisted status snapshot (IndexedDB) – survives app restart; important for iOS PWA when SW doesn't cache POST
        const snapshot = await this.storageService.getLarge<{ response: ApiResponse<StatusResponse>; timestamp: number }>('statusSnapshot');
        if (snapshot?.response && snapshot.timestamp) {
          const snapshotAge = Date.now() - snapshot.timestamp;
          if (snapshotAge < 24 * 60 * 60 * 1000) {
            this.logger.log('Using persisted status snapshot (offline mode, app restarted)');
            const statusData = (snapshot.response as any).DATA || snapshot.response;
            this.userId = statusData.user_id;
            this.companyId = statusData.company_id;
            this.userData = statusData.user_data || this.userData;
            this.companyData = statusData.company_data || this.companyData;
            this.userRole = (statusData.user_data as any)?.role ?? this.userRole;
            this.jobs = statusData.jobs || statusData.user_jobs ? this.normalizeJobsFromApi(statusData.jobs || statusData.user_jobs || {}) : this.jobs;
            this.tools = statusData.tools || statusData.products ? this.normalizeToolsFromApi((statusData.tools || statusData.products) as { [key: string]: Tool }) : this.tools;
            this.statusCache = { data: snapshot.response, timestamp: snapshot.timestamp };
            return snapshot.response;
          }
        }

        // If no cache available, try to construct response from stored data
        if (this.userData && this.companyData && this.jobs && this.tools) {
          this.logger.log('Constructing status response from stored data (offline mode)');
          // Construct a minimal status response from cached data
          const offlineResponse: ApiResponse<StatusResponse> = {
            STATUS: 'OK',
            DATA: {
              auth_staus: true,
              message: 'logged_in',
              user_id: this.userId || 0,
              company_id: this.companyId || undefined,
              tools: this.tools,
              user_products: {},
              user_data: this.userData,
              company_data: this.companyData,
              jobs: this.jobs
            }
          };
          return offlineResponse;
        }
        
        // If we can't construct a response, throw error
        throw new Error('Device is offline and no cached data available');
      }

      const data = {
        taskID: 'status',
        auth_secret: authSecret
      };

      const response = await this.apiService.post<StatusResponse>(data);
      
      // Handle both ApiResponse<StatusResponse> and direct StatusResponse formats
      // Check if response has DATA property (ApiResponse format) or is direct StatusResponse
      const statusData = (response as any).DATA || response;
      
      if (statusData?.auth_staus === true && statusData?.message === 'logged_in') {
        // Merge jobs from API with existing jobs from localStorage
        // If API returns jobs, use them (they are the latest)
        // If API doesn't return jobs, keep existing jobs from localStorage
        const apiJobs = statusData.jobs || statusData.user_jobs;
        
      
        if (apiJobs && Object.keys(apiJobs).length > 0) {
          // API returned jobs, use them (they are the latest)
          this.jobs = this.normalizeJobsFromApi(apiJobs);
        } else {
          // API didn't return jobs, use backup from localStorage
          this.jobs = this.normalizeJobsFromApi(backupJobs);
        }
        
        
        // Ensure jobs are saved to localStorage immediately after update
        if (this.jobs) {
          this.safeSetLocalStorage('jobs', this.jobs, true);
        }
        
        // API now sends "tools", fallback to "products" for migration
        const rawTools = (statusData.tools || statusData.products) as { [key: string]: Tool } | null;
        this.tools = rawTools ? this.normalizeToolsFromApi(rawTools) : null;
        this.userId = statusData.user_id; // Store user_id for tatUserData API calls
        
        // Extract and store company_id
        this.companyId = statusData.company_id || 
                        (typeof statusData.user_data?.company_id === 'number' ? statusData.user_data.company_id : null) ||
                        (typeof statusData.user_data?.companyID === 'number' ? statusData.user_data.companyID : null);
        
        // Separate User and Company data from response
        const responseData = statusData.user_data || {};
        
        // Update cache with correct structure (preserve ApiResponse format if it was ApiResponse)
        const cacheData = (response as any).DATA ? response : { STATUS: 'OK', DATA: statusData, DEBUG: (response as any).DEBUG };
        
        // Extract User data (app_bg, role, language, company_id, user_id, email, first_name, last_name)
        // Always use values from API response if available (including empty strings), otherwise preserve existing values
        // Check for property existence using 'in' operator to handle empty strings correctly
        this.userData = {
          email: this.userData?.email || responseData.email || undefined, // Preserve existing email or get from response
          app_bg: responseData.app_bg || '',
          role: ('role' in responseData) ? (responseData.role || undefined) : (this.userData?.role || undefined),
          language: ('language' in responseData) ? (responseData.language || undefined) : (this.userData?.language || undefined),
          company_id: this.companyId || this.companyData?.company_id || undefined,
          user_id: this.userId || undefined,
          first_name: ('first_name' in responseData) ? (responseData.first_name || undefined) : (this.userData?.first_name || undefined),
          last_name: ('last_name' in responseData) ? (responseData.last_name || undefined) : (this.userData?.last_name || undefined)
        };
        this.userRole = responseData.role || null;
        
        // Extract Company data (all company fields)
        // Check if API provides company_data separately, otherwise extract from user_data
        if (statusData.company_data) {
          this.companyData = statusData.company_data;
          // Extract company_id from company_data if not already set
          if (!this.companyId && this.companyData && this.companyData.company_id) {
            this.companyId = this.companyData.company_id;
          }
        } else {
          // Extract from user_data (backward compatibility)
          this.companyData = {
            company_id: this.companyId || 0,
            company: responseData.company || '',
            contact_name: responseData.contact_name || '',
            street: responseData.street || '',
            street_no: responseData.street_no || '',
            city: responseData.city || '',
            zip: responseData.zip || '',
            country: responseData.country || '',
            vat: responseData.vat || '',
            phone: responseData.phone || '',
            website: responseData.website || '',
            email_pp: responseData.email_pp || '',
            email_help: responseData.email_help || '',
            logo: responseData.logo || null,
            invarKEY: responseData.invarKEY || '',
            invarIV: responseData.invarIV || ''
          };
        }
        
        // Update cache
        const now = Date.now();
        this.statusCache = {
          data: cacheData,
          timestamp: now
        };

        // Persist full status snapshot for offline after app restart (iOS PWA: Service Worker does not cache POST)
        this.storageService.setLarge('statusSnapshot', { response: cacheData, timestamp: now }).catch(err => {
          this.errorHandler.handleStorageError('setLarge', 'statusSnapshot', err);
        });
        
        // Store separated data in localStorage (only essential data)
        // Jobs are already saved above, so we don't need to save them again here
        if (this.userData) {
          this.safeSetLocalStorage('userData', this.userData, true);
        }
        if (this.companyData) {
          this.safeSetLocalStorage('companyData', this.companyData, true);
        }
        if (this.tools) {
          this.safeSetLocalStorage('tools', this.tools, true);
        }
        // Note: companyId, userRole, userId are stored in userData, no need for separate entries
        
        // Set background from userData if available and different from current
        // Use setStylesBeforeNavigation() to avoid animation when status is refreshed
        if (this.userData?.app_bg) {
          const currentBg = this.backgroundSettingsService.getCurrentBackground();
          if (currentBg !== this.userData.app_bg) {
            this.backgroundSettingsService.setStylesBeforeNavigation(this.userData.app_bg);
          } else {
            // Background is already correct, but still set header/toolbar heights
            this.backgroundSettingsService.setStylesBeforeNavigation(this.userData.app_bg);
          }
        } else {
          // No userData.app_bg found, set default background and styles immediately
          this.backgroundSettingsService.setStylesBeforeNavigation('bg1');
        }
        
        // Load credits data in background (don't await to avoid blocking status fetch)
        this.getAvailableCredits().catch(error => {
          this.errorHandler.handleError('Credits-Load-After-Status', error);
        });
        
        // Return response in ApiResponse format for consistency
        // If response was already ApiResponse, return it as-is
        // Otherwise, wrap StatusResponse in ApiResponse format
        if ((response as any).STATUS) {
          return response as ApiResponse<StatusResponse>;
        } else {
          return { STATUS: 'OK', DATA: statusData, DEBUG: (response as any).DEBUG } as ApiResponse<StatusResponse>;
        }
      }
      
      // Cache even error responses (but with shorter validity)
      this.statusCache = {
        data: response,
        timestamp: Date.now()
      };
      
      return response;
    } catch (error) {
      this.errorHandler.handleError('Status-Fetch', error);
      // Return a minimal error response
      const errorResponse: ApiResponse<StatusResponse> = { 
        STATUS: 'ERROR',
        DATA: {
          auth_staus: false,
          message: 'Network error',
          tools: {},
          products: {}, // Legacy: kept for backward compatibility
          user_id: 0,
          user_products: {},
          user_data: {} as UserData,
          jobs: {}
        },
        DEBUG: { runtime: '0', memory: '0' }
      };
      // Don't cache error responses
      return errorResponse;
    }
  }

  /**
   * @deprecated Overwrites the results baseline without change detection (new results would not
   * count for the home badge). Use DataStateService.refreshResults() instead.
   */
  async fetchTatUserData(): Promise<{ [key: string]: UserDataItem } | null> {
    try {
      const jobs = this.getJobs();
      if (!jobs || Object.keys(jobs).length === 0) {
        return null;
      }

      // Check if offline - if so, try to return cached/stored data
      if (this.networkService.isOffline()) {
        this.logger.log('Device is offline, attempting to use cached tatUserData');
        
        // Try to load from storage
        try {
          let storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('userResults');
          if (!storedUserResults) {
            storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('tatUserDataByTool');
            if (!storedUserResults) {
              storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('tatUserDataByProduct');
            }
          }
          
          if (storedUserResults && typeof storedUserResults === 'object' && !Array.isArray(storedUserResults)) {
            this.userResults = storedUserResults;
            // Merge data for backward compatibility
            const mergedData: { [key: string]: UserDataItem } = {};
            Object.values(storedUserResults).forEach(toolData => {
              Object.assign(mergedData, toolData);
            });
            this.logger.log('Using stored tatUserData (offline mode)');
            return mergedData;
          }
        } catch (error) {
          this.errorHandler.handleStorageError('getLarge', 'userResults', error);
        }
        
        // If we have userResults in memory, use them
        if (this.userResults) {
          const mergedData: { [key: string]: UserDataItem } = {};
          Object.values(this.userResults).forEach(toolData => {
            Object.assign(mergedData, toolData);
          });
          this.logger.log('Using in-memory tatUserData (offline mode)');
          return mergedData;
        }
        
        // If no cached data available, return null
        this.logger.warn('No cached tatUserData available (offline mode)');
        return null;
      }

      // Get the user_id from jobs or from stored userId
      // Try to get from first job first, fallback to stored userId from status response
      const firstJob = Object.values(jobs)[0];
      let userID = firstJob?.user_id;
      
      // If user_id is not in job, use the stored userId from status response
      if (!userID && this.userId) {
        userID = String(this.userId);
      }
      
      if (!userID) {
        throw new Error('No user ID found in jobs or status');
      }

      // Get auth secret for API call
      const authSecret = this.getAuthSecret();
      if (!authSecret) {
        throw new Error('No auth secret available');
      }

      // Use new API endpoint via ApiService.post() (consistent with other API calls like getAvailableCredits)
      // POST is used to keep parameters in request body, not visible in URL
      const data: GetAllTatUserDataRequest = {
        taskID: 'getAllTatUserData',
        auth_secret: authSecret,
        tatID: String(userID)
      };

      // Note: The response structure may be ApiResponse<T> or direct GetAllTatUserDataResponse
      // The apiService.post() returns ApiResponse<T>, but the actual API might return direct structure
      const apiResponse = await this.apiService.post<GetAllTatUserDataResponse>(data);
      
      // Extract the actual response data
      // Try DATA first (if wrapped in ApiResponse), then check if response itself is GetAllTatUserDataResponse
      let response: GetAllTatUserDataResponse;
      
      if ((apiResponse as any).DATA && typeof (apiResponse as any).DATA === 'object' && (apiResponse as any).DATA.auth_status !== undefined) {
        // Response is wrapped in ApiResponse format: { STATUS: "...", DATA: {...} }
        response = (apiResponse as any).DATA;
      } else if ((apiResponse as any).auth_status !== undefined) {
        // Response is direct GetAllTatUserDataResponse format: { auth_status: ..., tatID: ..., tools: ... }
        response = apiResponse as any;
      } else {
        // Unexpected structure - log for debugging
        this.logger.warn('Unexpected API response structure for getAllTatUserData:', apiResponse);
        throw new Error(`Unexpected API response structure. Expected GetAllTatUserDataResponse but got: ${JSON.stringify(apiResponse).substring(0, 200)}`);
      }
      
      // Check auth status
      if (!response || response.auth_status !== true) {
        const errorMsg = response?.auth_status === false 
          ? 'Authentication failed: auth_status is false' 
          : 'Authentication failed: auth_status is missing or invalid';
        this.logger.error(`[TatUserData-Fetch] ${errorMsg}`, { response, apiResponse });
        throw new Error(errorMsg);
      }

      // Handle errors from API response
      if (response.errors && Object.keys(response.errors).length > 0) {
        Object.entries(response.errors).forEach(([toolId, errorMessage]) => {
          this.errorHandler.handleError(`TatUserData-Fetch-${toolId}`, new Error(errorMessage));
        });
      }

      // Convert response.tools to dataByTool format
      // Response structure: { tools: { toolKey: { uid: UserDataItem } } }
      const dataByTool: { [toolKey: string]: { [uid: string]: UserDataItem } } = {};
      
      if (response.tools && typeof response.tools === 'object') {
        Object.entries(response.tools).forEach(([toolKey, toolData]) => {
          if (toolData && typeof toolData === 'object' && !Array.isArray(toolData)) {
            dataByTool[toolKey] = toolData as { [uid: string]: UserDataItem };
          }
        });
      }

      // Store clustered data by tool
      this.userResults = dataByTool;
      
      // Remove old key for migration
      localStorage.removeItem('tatUserDataByTool');
      // Store userResults with quota handling
      this.safeSetLocalStorage('userResults', dataByTool, false);
      
      // Return merged data for backward compatibility
      const mergedData: { [key: string]: UserDataItem } = {};
      Object.values(dataByTool).forEach(toolData => {
        Object.assign(mergedData, toolData);
      });
      return mergedData;
    } catch (error) {
      this.errorHandler.handleError('TatUserData-Fetch', error);
      return null;
    }
  }

  // Method to fetch tat user data and return changes with caching
  /**
   * Fetches TAT user data and detects changes compared to previous fetch
   * Uses caching to avoid excessive API calls
   * 
   * @param forceRefresh - If true, bypasses cache and fetches fresh data from API
   * @returns Promise resolving to object containing:
   *   - data: User data items organized by product name and UID, or null if no data
   *   - changes: Object containing detected changes (new entries, level changes, etc.)
   * 
   * @throws {Error} If user ID cannot be determined or API call fails
   * 
   * @example
   * ```typescript
   * const result = await authService.fetchTatUserDataWithChanges();
   * if (result.changes.hasChanges) {
   *   // Handle new entries or level changes
   * }
   * ```
   */
  async fetchTatUserDataWithChanges(forceRefresh: boolean = false): Promise<{ data: { [key: string]: UserDataItem } | null; changes: TatUserDataChanges }> {
    // Ensure userResults are loaded from storage before proceeding
    // This is critical for accurate change detection
    if (this.userResultsLoadPromise) {
      await this.userResultsLoadPromise;
      this.userResultsLoadPromise = null; // Clear after first wait
    }
    
    // Also ensure userResults is loaded if it's still null
    if (!this.userResults) {
      await this.loadUserResultsFromStorage();
    }
    
    // Check cache first
    if (!forceRefresh && this.tatUserDataCache.data && this.tatUserDataCache.timestamp > 0) {
      const cacheAge = Date.now() - this.tatUserDataCache.timestamp;
      if (cacheAge < this.CACHE_DURATION_TAT_DATA) {
        // Return cached data and update local state
        const cachedResult = this.tatUserDataCache.data;
        // Ensure userResults is set (it should already be from storage or previous fetch)
        // If not, try to load from storage
        if (!this.userResults) {
          try {
            // Try new key first, then fallback to old keys for migration
            let storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('userResults');
            if (!storedUserResults) {
              storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('tatUserDataByTool');
              if (!storedUserResults) {
                storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('tatUserDataByProduct');
              }
              if (storedUserResults) {
                await this.storageService.setLarge('userResults', storedUserResults);
                await this.storageService.removeLarge('tatUserDataByTool').catch(() => {});
                await this.storageService.removeLarge('tatUserDataByProduct').catch(() => {});
              }
            }
            if (storedUserResults && typeof storedUserResults === 'object' && !Array.isArray(storedUserResults)) {
              this.userResults = storedUserResults;
            }
          } catch (error) {
            this.errorHandler.handleStorageError('getLarge', 'userResults', error);
          }
        }
        // Ensure cached data is also saved to IndexedDB for persistence
        if (this.userResults && !forceRefresh) {
          await this.safeSetStorage('userResults', this.userResults, false, true).catch(err => {
            this.errorHandler.handleStorageError('setLarge', 'userResults', err);
          });
        }
        // Restore changes state (without dropping other unseen changes)
        this.addUnseenTatUserDataChanges(cachedResult.changes);
        return cachedResult;
      }
    }

    // If a fetch is already in progress, wait for it
    if (this.tatUserDataFetchPromise) {
      return this.tatUserDataFetchPromise;
    }

    // Start new fetch
    this.tatUserDataFetchPromise = this.fetchTatUserDataWithChangesInternal();
    
    try {
      const result = await this.tatUserDataFetchPromise;
      return result;
    } finally {
      // Clear promise after completion
      this.tatUserDataFetchPromise = null;
    }
  }

  private async fetchTatUserDataWithChangesInternal(): Promise<{ data: { [key: string]: UserDataItem } | null; changes: TatUserDataChanges }> {
    try {
      const jobs = this.getJobs();
      if (!jobs || Object.keys(jobs).length === 0) {
        const emptyResult = { data: null, changes: { newEntries: [], levelChanges: [], hasChanges: false } };
        this.tatUserDataCache = { data: emptyResult, timestamp: Date.now() };
        return emptyResult;
      }

      // Check if offline - if so, try to return cached/stored data
      if (this.networkService.isOffline()) {
        this.logger.log('Device is offline, attempting to use cached tatUserData');
        
        // Try to load from storage
        try {
          let storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('userResults');
          if (!storedUserResults) {
            storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('tatUserDataByTool');
            if (!storedUserResults) {
              storedUserResults = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('tatUserDataByProduct');
            }
          }
          
          if (storedUserResults && typeof storedUserResults === 'object' && !Array.isArray(storedUserResults)) {
            this.userResults = storedUserResults;
            // Merge data for backward compatibility
            const mergedData: { [key: string]: UserDataItem } = {};
            Object.values(storedUserResults).forEach(toolData => {
              Object.assign(mergedData, toolData);
            });
            
            // Return with no changes (we can't detect changes offline)
            const offlineResult = { 
              data: mergedData, 
              changes: { newEntries: [], levelChanges: [], hasChanges: false } 
            };
            this.logger.log('Using stored tatUserData (offline mode)');
            return offlineResult;
          }
        } catch (error) {
          this.errorHandler.handleStorageError('getLarge', 'userResults', error);
        }
        
        // If we have userResults in memory, use them
        if (this.userResults) {
          const mergedData: { [key: string]: UserDataItem } = {};
          Object.values(this.userResults).forEach(toolData => {
            Object.assign(mergedData, toolData);
          });
          const offlineResult = { 
            data: mergedData, 
            changes: { newEntries: [], levelChanges: [], hasChanges: false } 
          };
          this.logger.log('Using in-memory tatUserData (offline mode)');
          return offlineResult;
        }
        
        // If no cached data available, return empty result
        this.logger.warn('No cached tatUserData available (offline mode)');
        const emptyResult = { data: null, changes: { newEntries: [], levelChanges: [], hasChanges: false } };
        return emptyResult;
      }

      // Get the user_id from jobs or from stored userId
      // Try to get from first job first, fallback to stored userId from status response
      const firstJob = Object.values(jobs)[0];
      let userID = firstJob?.user_id;
      
      // If user_id is not in job, use the stored userId from status response
      if (!userID && this.userId) {
        userID = String(this.userId);
      }
      
      if (!userID) {
        throw new Error('No user ID found in jobs or status');
      }

      // Get auth secret for API call
      const authSecret = this.getAuthSecret();
      if (!authSecret) {
        throw new Error('No auth secret available');
      }

      // Use new API endpoint via ApiService.post() (consistent with other API calls like getAvailableCredits)
      // POST is used to keep parameters in request body, not visible in URL
      const data: GetAllTatUserDataRequest = {
        taskID: 'getAllTatUserData',
        auth_secret: authSecret,
        tatID: String(userID)
      };

      // Note: The response structure may be ApiResponse<T> or direct GetAllTatUserDataResponse
      // The apiService.post() returns ApiResponse<T>, but the actual API might return direct structure
      const apiResponse = await this.apiService.post<GetAllTatUserDataResponse>(data);
      
      // Extract the actual response data
      // Try DATA first (if wrapped in ApiResponse), then check if response itself is GetAllTatUserDataResponse
      let response: GetAllTatUserDataResponse;
      
      if ((apiResponse as any).DATA && typeof (apiResponse as any).DATA === 'object' && (apiResponse as any).DATA.auth_status !== undefined) {
        // Response is wrapped in ApiResponse format: { STATUS: "...", DATA: {...} }
        response = (apiResponse as any).DATA;
      } else if ((apiResponse as any).auth_status !== undefined) {
        // Response is direct GetAllTatUserDataResponse format: { auth_status: ..., tatID: ..., tools: ... }
        response = apiResponse as any;
      } else {
        // Unexpected structure - log for debugging
        this.logger.warn('Unexpected API response structure for getAllTatUserData:', apiResponse);
        throw new Error(`Unexpected API response structure. Expected GetAllTatUserDataResponse but got: ${JSON.stringify(apiResponse).substring(0, 200)}`);
      }
      
      // Check auth status
      if (!response || response.auth_status !== true) {
        const errorMsg = response?.auth_status === false 
          ? 'Authentication failed: auth_status is false' 
          : 'Authentication failed: auth_status is missing or invalid';
        this.logger.error(`[TatUserData-Fetch-WithChanges] ${errorMsg}`, { response, apiResponse });
        throw new Error(errorMsg);
      }

      // Handle errors from API response
      if (response.errors && Object.keys(response.errors).length > 0) {
        Object.entries(response.errors).forEach(([toolId, errorMessage]) => {
          this.errorHandler.handleError(`TatUserData-Fetch-${toolId}`, new Error(errorMessage));
        });
      }

      // Convert response.tools to dataByTool format
      // Response structure: { tools: { toolKey: { uid: UserDataItem } } }
      const dataByTool: { [toolKey: string]: { [uid: string]: UserDataItem } } = {};
      
      if (response.tools && typeof response.tools === 'object') {
        // Sort tool keys so DM is applied last (to prevent overwriting by other tools when merging)
        const sortedToolKeys = Object.keys(response.tools).sort((a, b) => {
          const keyA = a.toLowerCase();
          const keyB = b.toLowerCase();
          if (keyA === 'dm') return 1;
          if (keyB === 'dm') return -1;
          return 0;
        });

        // Apply in sorted order so DM (last) overwrites any overlapping keys
        sortedToolKeys.forEach(toolKey => {
          const toolData = response.tools[toolKey];
          if (toolData && typeof toolData === 'object' && !Array.isArray(toolData)) {
            dataByTool[toolKey] = toolData as { [uid: string]: UserDataItem };
          }
        });
      }
      
      // Load baseline from storage before comparing to ensure accurate change detection
      // This prevents detecting changes for data that was already there
      if (!this.userResults || Object.keys(this.userResults).length === 0) {
        // Try to load baseline from storage if not in memory
        try {
          let storedBaseline = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('userResults');
          if (!storedBaseline) {
            storedBaseline = await this.storageService.getLarge<{ [toolName: string]: { [uid: string]: UserDataItem } }>('tatUserDataByTool');
          }
          if (storedBaseline && typeof storedBaseline === 'object' && !Array.isArray(storedBaseline)) {
            this.userResults = storedBaseline;
          }
        } catch (error) {
          this.errorHandler.handleStorageError('getLarge', 'userResults', error);
        }
      }
      
      const changes = this.compareTatUserDataByTool(dataByTool);
      
      // Always take over the fetched data (changes are already detected above), otherwise
      // entries removed on the server (e.g. foreign-tool results) stay visible until reload
      this.userResults = dataByTool;

      // Remove old key for migration
      localStorage.removeItem('tatUserDataByTool');
      // Store userResults to IndexedDB (large data) to ensure persistence
      await this.safeSetStorage('userResults', dataByTool, false, true).catch(err => {
        this.errorHandler.handleStorageError('setLarge', 'userResults', err);
      });
      
      this.addUnseenTatUserDataChanges(changes);
      
      // Return merged data for backward compatibility
      const mergedData: { [key: string]: UserDataItem } = {};
      Object.values(dataByTool).forEach(toolData => {
        Object.assign(mergedData, toolData);
      });
      
      const result = { data: mergedData, changes };
      
      // Update cache
      this.tatUserDataCache = {
        data: result,
        timestamp: Date.now()
      };
      
      return result;
    } catch (error) {
      this.errorHandler.handleError('TatUserData-Fetch-WithChanges', error);
      const errorResult = { data: null, changes: { newEntries: [], levelChanges: [], hasChanges: false } };
      // Don't cache error responses
      return errorResult;
    }
  }

  // Method to compare new tat user data with stored data and detect changes
  compareTatUserData(newData: { [key: string]: UserDataItem } | null): TatUserDataChanges {
    const changes: TatUserDataChanges = {
      newEntries: [],
      levelChanges: [],
      hasChanges: false
    };

    if (!newData) {
      return changes;
    }

    const oldDataByTool = this.userResults || {};
    const oldData: { [key: string]: UserDataItem } = {};
    Object.values(oldDataByTool).forEach(toolData => {
      Object.assign(oldData, toolData);
    });
    
    if (Object.keys(oldData).length === 0) {
      changes.hasChanges = false;
      return changes;
    }

    const hasLevel2 = (item: UserDataItem): boolean => {
      if (!item.lvl) return false;
      const level = Number(item.lvl);
      if (!Number.isNaN(level)) {
        return level === 2;
      }
      const levelStr = String(item.lvl).toLowerCase();
      return levelStr === 'green' || levelStr === '2';
    };

    Object.values(newData).forEach(newItem => {
      if (!hasLevel2(newItem)) {
        return;
      }

      const oldItem = oldData[newItem.uid];
      
      if (!oldItem) {
        changes.newEntries.push(newItem);
      } else if (oldItem.lvl !== newItem.lvl && hasLevel2(newItem)) {
        changes.levelChanges.push({
          item: newItem,
          oldLevel: oldItem.lvl,
          newLevel: newItem.lvl
        });
      }
    });

    changes.hasChanges = changes.newEntries.length > 0 || changes.levelChanges.length > 0;
    return changes;
  }

  // Method to compare new tat user data with stored data by tool (more accurate for multi-tool scenarios)
  compareTatUserDataByTool(
    newDataByTool: { [toolName: string]: { [uid: string]: UserDataItem } }
  ): TatUserDataChanges {
    const changes: TatUserDataChanges = {
      newEntries: [],
      levelChanges: [],
      hasChanges: false
    };

    if (!newDataByTool || Object.keys(newDataByTool).length === 0) {
      return changes;
    }

    // Use stored baseline - if userResults is not loaded, it means this is the first time
    // and we should not detect changes (all data is "new" but shouldn't trigger notifications)
    const oldDataByTool = this.userResults || {};

    // If no baseline exists, don't detect changes (first load scenario)
    // This prevents showing notifications for data that was already there
    if (Object.keys(oldDataByTool).length === 0) {
      changes.hasChanges = false;
      return changes;
    }

    const hasLevel2 = (item: UserDataItem): boolean => {
      if (!item.lvl) return false;
      const level = Number(item.lvl);
      if (!Number.isNaN(level)) {
        return level === 2;
      }
      const levelStr = String(item.lvl).toLowerCase();
      return levelStr === 'green' || levelStr === '2';
    };

    Object.entries(newDataByTool).forEach(([toolName, toolData]) => {
      const oldToolData = oldDataByTool[toolName] || {};

      Object.values(toolData).forEach(newItem => {
        if (!hasLevel2(newItem)) {
          return;
        }

        const oldItem = oldToolData[newItem.uid];

        if (!oldItem) {
          changes.newEntries.push({ ...newItem, tool: toolName });
        } else if (oldItem.lvl !== newItem.lvl && hasLevel2(newItem)) {
          changes.levelChanges.push({
            item: { ...newItem, tool: toolName },
            oldLevel: oldItem.lvl,
            newLevel: newItem.lvl
          });
        }
      });
    });

    changes.hasChanges = changes.newEntries.length > 0 || changes.levelChanges.length > 0;
    return changes;
  }

  // Method to update user data (app_bg, role, first_name, last_name)
  async updateUserData(userData: Partial<UserData>): Promise<AuthResponse> {
    try {
      const authSecret = this.getAuthSecret();
      if (!authSecret) {
        throw new Error('No authentication secret found');
      }

      // Only send fields that are actually provided in userData
      // This prevents overwriting existing values with empty strings
      const data: ApiRequest = {
        taskID: 'storeUserData',
        auth_secret: authSecret
      };
      
      // Only include fields that are actually defined in userData
      if (userData.app_bg !== undefined) {
        data['app_bg'] = userData.app_bg;
      }
      if (userData.role !== undefined) {
        data['role'] = userData.role;
      }
      if (userData.language !== undefined) {
        data['language'] = userData.language;
      }
      if (userData.first_name !== undefined) {
        data['first_name'] = userData.first_name;
      }
      if (userData.last_name !== undefined) {
        data['last_name'] = userData.last_name;
      }

      // Build description for queue metadata
      let description = 'Benutzerdaten aktualisieren';
      if (userData.app_bg !== undefined) {
        description = `Hintergrund ändern zu "${userData.app_bg}"`;
      } else if (userData.language !== undefined) {
        description = `Sprache ändern zu "${userData.language}"`;
      } else if (userData.role !== undefined) {
        description = `Rolle ändern zu "${userData.role}"`;
      }

      const response: AuthResponse = await this.apiService.post(data, '', {
        description: description,
        page: 'settings'
      });
      
      // Validate response structure
      if (!response || !response.STATUS || typeof response.STATUS !== 'string') {
        const errorMessage = 'Invalid response from server';
        throw new Error(errorMessage);
      }
      
      // Handle queued response (offline)
      if (response.STATUS === 'QUEUED') {
        // Update local userData immediately for offline use
        if (this.userData) {
          this.userData = {
            ...this.userData,
            ...(userData.app_bg !== undefined && { app_bg: userData.app_bg }),
            ...(userData.role !== undefined && { role: userData.role }),
            ...(userData.language !== undefined && { language: userData.language }),
            ...(userData.first_name !== undefined && { first_name: userData.first_name }),
            ...(userData.last_name !== undefined && { last_name: userData.last_name }),
            ...(userData.email !== undefined && { email: userData.email }),
            ...(userData.company_id !== undefined && { company_id: userData.company_id }),
            ...(userData.user_id !== undefined && { user_id: userData.user_id })
          } as UserData;
        }
        
        // Update localStorage for offline persistence
        this.safeSetLocalStorage('userData', this.userData, true);
        
        // Return QUEUED status - caller should handle it
        return response;
      }
      
      // Accept both -> 1 and -> 0 as success (0 means no rows updated, but still success)
      if (response.STATUS.startsWith('SUCCESS-storeUserData')) {
        // Update local userData - only update fields that are actually provided
        // This preserves existing values for fields not in the update
        if (this.userData) {
          this.userData = {
            ...this.userData,
            ...(userData.app_bg !== undefined && { app_bg: userData.app_bg }),
            ...(userData.role !== undefined && { role: userData.role }),
            ...(userData.language !== undefined && { language: userData.language }),
            ...(userData.first_name !== undefined && { first_name: userData.first_name }),
            ...(userData.last_name !== undefined && { last_name: userData.last_name }),
            ...(userData.email !== undefined && { email: userData.email }),
            ...(userData.company_id !== undefined && { company_id: userData.company_id }),
            ...(userData.user_id !== undefined && { user_id: userData.user_id })
          } as UserData;
        } else {
          // If userData doesn't exist yet, create it with provided values
          this.userData = {
            app_bg: userData.app_bg || '',
            role: userData.role,
            language: userData.language,
            first_name: userData.first_name,
            last_name: userData.last_name,
            email: userData.email,
            company_id: userData.company_id,
            user_id: userData.user_id
          } as UserData;
        }
        
        // Invalidate status cache since user data changed
        this.invalidateStatusCache();
        
        // Update localStorage (only userData)
        this.safeSetLocalStorage('userData', this.userData, true);
        
        return response;
      } else {
        // Handle field-specific validation errors
        const errorMessage = this.errorMessagesService.getFieldErrorMessage(response);
        
        return { 
          STATUS: response.STATUS,
          DATA: {
            auth_secret: '',
            message: errorMessage,
            status: {
              user_id: 0,
              tools: {},
              products: {}, // Legacy: kept for backward compatibility
              user_data: {} as UserData,
              user_products: {},
              jobs: {}
            }
          },
          DEBUG: response.DEBUG || { runtime: '0', memory: '0' }
        };
      }
    } catch (error) {
      this.errorHandler.handleAuthError('updateUserData', error);
      const errorMessage = this.errorMessagesService.getErrorMessage('NETWORK') || 
                          this.errorHandler.getUserFriendlyMessage(error);
      
      return { 
        STATUS: 'NETWORK',
        DATA: {
          auth_secret: '',
          message: errorMessage,
          status: {
            user_id: 0,
            tools: {},
            products: {}, // Legacy: kept for backward compatibility
            user_data: {} as UserData,
            user_products: {},
            user_jobs: {}
          }
        },
        DEBUG: { runtime: '0', memory: '0' }
      };
    }
  }

  // Method to update company data
  async updateCompanyData(companyData: Partial<Company>): Promise<AuthResponse> {
    try {
      const authSecret = this.getAuthSecret();
      if (!authSecret) {
        throw new Error('No authentication secret found');
      }

      const data = {
        taskID: 'storeCompanyData',
        auth_secret: authSecret,
        company: companyData.company || '',
        contact_name: companyData.contact_name || '',
        street: companyData.street || '',
        street_no: companyData.street_no || '',
        city: companyData.city || '',
        zip: companyData.zip || '',
        country: companyData.country || '',
        vat: companyData.vat || '',
        phone: companyData.phone || '',
        website: companyData.website || '',
        email_pp: companyData.email_pp || '',
        email_help: companyData.email_help || ''
      };

      const response: AuthResponse = await this.apiService.post(data, '', {
        description: 'Firmendaten speichern',
        page: 'settings'
      });
      
      // Validate response structure
      if (!response || !response.STATUS || typeof response.STATUS !== 'string') {
        const errorMessage = 'Invalid response from server';
        throw new Error(errorMessage);
      }
      
      // Handle queued response (offline)
      if (response.STATUS === 'QUEUED') {
        // Return QUEUED status - caller should handle it
        return response;
      }
      
      // Accept both -> 1 and -> 0 as success (0 means no rows updated, but still success)
      if (response.STATUS.startsWith('SUCCESS-storeCompanyData')) {
        // Update local companyData
        this.companyData = { ...this.companyData, ...companyData } as Company;
        
        // Invalidate status cache since company data changed
        this.invalidateStatusCache();
        
        // Update localStorage (only companyData)
        this.safeSetLocalStorage('companyData', this.companyData, true);
        
        return response;
      } else {
        // Handle field-specific validation errors
        const errorMessage = this.errorMessagesService.getFieldErrorMessage(response);
        
        return { 
          STATUS: response.STATUS,
          DATA: {
            auth_secret: '',
            message: errorMessage,
            status: {
              user_id: 0,
              tools: {},
              products: {}, // Legacy: kept for backward compatibility
              user_data: {} as UserData,
              user_products: {},
              jobs: {}
            }
          },
          DEBUG: response.DEBUG || { runtime: '0', memory: '0' }
        };
      }
    } catch (error) {
      this.errorHandler.handleAuthError('updateCompanyData', error);
      const errorMessage = this.errorMessagesService.getErrorMessage('NETWORK') || 
                          this.errorHandler.getUserFriendlyMessage(error);
      
      return { 
        STATUS: 'NETWORK',
        DATA: {
          auth_secret: '',
          message: errorMessage,
          status: {
            user_id: 0,
            tools: {},
            products: {}, // Legacy: kept for backward compatibility
            user_data: {} as UserData,
            user_products: {},
            user_jobs: {}
          }
        },
        DEBUG: { runtime: '0', memory: '0' }
      };
    }
  }

  // Method to store company logo
  async storeCompanyLogo(logoFile: File): Promise<AuthResponse> {
    try {
      const authSecret = this.getAuthSecret();
      if (!authSecret) {
        throw new Error('No authentication secret found');
      }

      // Create FormData for multipart/form-data upload
      const formData = new FormData();
      formData.append('taskID', 'storeCompanyLogo');
      formData.append('auth_secret', authSecret);
      formData.append('logo', logoFile);

      const response: AuthResponse = await this.apiService.post(formData, '', {
        description: 'Firmenlogo hochladen',
        page: 'settings'
      });
      
      // Handle queued response (offline)
      if (response.STATUS === 'QUEUED') {
        // Return QUEUED status - caller should handle it
        return response;
      }
      
      // Validate response structure - only check if response exists
      // STATUS can be 'ERROR' (valid API response) or 'SUCCESS' variants
      if (!response) {
        const errorMessage = 'Invalid response from server';
        return {
          STATUS: 'ERROR',
          DATA: {
            auth_secret: '',
            message: errorMessage,
            status: {
              user_id: 0,
              tools: {},
              products: {}, // Legacy: kept for backward compatibility
              user_data: {} as UserData,
              user_products: {},
              jobs: {}
            }
          },
          DEBUG: { runtime: '0', memory: '0' }
        };
      }
      
      // Ensure STATUS exists and is a string before calling startsWith
      const status = response.STATUS;
      if (!status || typeof status !== 'string') {
        const errorMessage = response.DATA?.message || 'Invalid response format from server';
        return {
          STATUS: 'ERROR',
          DATA: {
            auth_secret: '',
            message: errorMessage,
            status: {
              user_id: 0,
              tools: {},
              products: {}, // Legacy: kept for backward compatibility
              user_data: {} as UserData,
              user_products: {},
              jobs: {}
            }
          },
          DEBUG: response.DEBUG || { runtime: '0', memory: '0' }
        };
      }
      
      // Accept both -> 1 and -> 0 as success
      if (status.startsWith('SUCCESS-storeCompanyLogo') || status.startsWith('SUCCESS')) {
        // Invalidate status cache since company data changed
        this.invalidateStatusCache();
        
        // Reload company data to get updated logo
        await this.fetchStatus(true);
        
        // fetchStatus() already updates localStorage via safeSetLocalStorage
        // No need to do it again here - keep it simple like the working version
        
        return response;
      } else {
        const errorMessage = this.errorMessagesService.getErrorMessage(response.STATUS) || 
                            response.DATA?.message || 
                            'Failed to upload logo';
        
        return { 
          STATUS: response.STATUS,
          DATA: {
            auth_secret: '',
            message: errorMessage,
            status: {
              user_id: 0,
              tools: {},
              products: {}, // Legacy: kept for backward compatibility
              user_data: {} as UserData,
              user_products: {},
              jobs: {}
            }
          },
          DEBUG: response.DEBUG || { runtime: '0', memory: '0' }
        };
      }
    } catch (error) {
      this.errorHandler.handleAuthError('storeCompanyLogo', error);
      const errorMessage = this.errorMessagesService.getErrorMessage('NETWORK') || 
                          this.errorHandler.getUserFriendlyMessage(error);
      
      return { 
        STATUS: 'NETWORK',
        DATA: {
          auth_secret: '',
          message: errorMessage,
          status: {
            user_id: 0,
            tools: {},
            products: {}, // Legacy: kept for backward compatibility
            user_data: {} as UserData,
            user_products: {},
            user_jobs: {}
          }
        },
        DEBUG: { runtime: '0', memory: '0' }
      };
    }
  }
}
